import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import useUserDataStore from 'src/store/user';
import { useSystemStore } from 'src/store/SystemStore';
import useAuthStore from 'src/auth/store/firebase/auth-store';

// ----------------------------------------------------------------------

// axiosInstance.interceptors.response.use(
//   (res) => res,
//   (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
// );

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  console.log('[Fetcher] Starting fetch for:', url);

  try {
    const { getToken, authenticated } = useAuthStore.getState();

    // Log auth state for debugging
    console.log('[Fetcher] Auth state check:', { authenticated, url });

    if (!authenticated) {
      console.error('[Fetcher] ⚠️ User not authenticated - cannot fetch');
      const authError = new Error('User not authenticated');
      (authError as any).isAuthError = true;
      throw authError;
    }

    console.log('[Fetcher] Getting auth token...');
    let authToken: string | null = null;

    try {
      authToken = await getToken();
    } catch (tokenError) {
      console.error('[Fetcher] ⚠️ Failed to get auth token:', tokenError);
      const authError = new Error('Failed to get auth token');
      (authError as any).isAuthError = true;
      (authError as any).originalError = tokenError;
      throw authError;
    }

    if (!authToken) {
      console.error('[Fetcher] ⚠️ No auth token available');
      const authError = new Error('authToken is not found');
      (authError as any).isAuthError = true;
      throw authError;
    }

    console.log('[Fetcher] ✅ Auth token obtained successfully');

    const axiosInstance = axios.create({
      baseURL: useSystemStore.getState().baseUrl,
      timeout: 30000, // 30 second timeout to prevent hanging indefinitely
    });

    console.log('[Fetcher] Making API request to:', url);
    const res = await axiosInstance.get(url, {
      headers: { Authorization: `Bearer ${authToken}`, ...getMetaHeaders() },
      ...config,
    });

    console.log('[Fetcher] ✅ Success for:', url);
    const data = res.data.result;
    return data;
  } catch (error) {
    console.error('[Fetcher] ❌ Error fetching:', url);

    // Check if this is an axios error with a 401 status
    const axiosError = error as AxiosError;
    if (axiosError.isAxiosError && axiosError.response?.status === 401) {
      console.error('[Fetcher] ⚠️ 401 Unauthorized - auth error detected');
      const authError = new Error('401 Unauthorized');
      (authError as any).isAuthError = true;
      (authError as any).originalError = error;
      throw authError;
    }

    // Log more details about the error
    if (axiosError.isAxiosError) {
      console.error('[Fetcher] Axios error details:', {
        status: axiosError.response?.status,
        statusText: axiosError.response?.statusText,
        data: axiosError.response?.data,
      });
    } else {
      console.error('[Fetcher] Non-axios error:', error);
    }

    throw error;
  }
};

// ----------------------------------------------------------------------
export const apiSender = async (
  url: string,
  method: ApiMethods,
  data: any,
  formatResponse = true
) => {
  const axiosInstance = axios.create({ baseURL: useSystemStore.getState().baseUrl });
  const { getToken } = useAuthStore.getState();
  const authToken = await getToken();
  if (!authToken) {
    throw new Error('authToken is not found');
  }
  try {
    // Update this to all methods
    let res;
    if (method === ApiMethods.GET) {
      res = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${authToken}`, ...getMetaHeaders() },
      });
    } else if (method === ApiMethods.POST) {
      res = await axiosInstance.post(url, data, {
        headers: { Authorization: `Bearer ${authToken}`, ...getMetaHeaders() },
      });
    } else if (method === ApiMethods.PUT) {
      res = await axiosInstance.put(url, data, {
        headers: { Authorization: `Bearer ${authToken}`, ...getMetaHeaders() },
      });
    } else if (method === ApiMethods.DELETE) {
      res = await axiosInstance.delete(url, {
        headers: { Authorization: `Bearer ${authToken}`, ...getMetaHeaders() },
        data, // Include request body for DELETE requests
      });
    } else if (method === ApiMethods.PATCH) {
      res = await axiosInstance.patch(url, data, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } else {
      throw new Error('Method not supported');
    }
    // const res = await axiosInstance.(url, data, {
    //   headers: { Authorization: `Bearer ${authToken}` },
    // });

    if (res?.status >= 300) {
      return null;
    }
    return formatResponse ? (res.data.results ?? res.data.result) : res.data;
  } catch (error) {
    return null;
  }
};

const getMetaHeaders = (): any => {
  const { baseUrl, currentDevice, currentDeviceType, appVersion, baseAthUrl } =
    useSystemStore.getState();
  const { business } = useUserDataStore.getState();

  // Encode device data to Base64 to avoid non-ASCII character issues in headers
  const deviceDataStr = JSON.stringify(currentDevice);
  // Use a safer Base64 encoding that handles Unicode properly
  const encodedDeviceData = btoa(
    encodeURIComponent(deviceDataStr).replace(/%([0-9A-F]{2})/g, (match, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )
  );

  // Safely encode business name to handle special characters
  const safeBusinessName = business?.name
    ? encodeURIComponent(business.name).replace(/%20/g, ' ')
    : undefined;

  // Replace "Latest" with "999.999.999" for web devices to ensure they get routed to highest version
  const normalizeVersion = (version: string | undefined): string | undefined => {
    if (version?.toLowerCase() === 'latest') {
      return '999.999.999';
    }
    return version;
  };

  return {
    'X-Current-Device': currentDevice.id,
    // business name and business id
    'X-Business-Name': safeBusinessName,
    'X-Business-Id': business?.id,
    'X-Current-Device-data': encodedDeviceData,
    'X-Current-Device-data-encoding': 'base64', // Indicate encoding type for backend
    'X-Current-Device-Type': currentDeviceType,
    'accept-version': normalizeVersion(appVersion.current),
    'minimum-version': normalizeVersion(appVersion.minimum),
    'X-Base-Ath-Url': baseAthUrl,
  };
};

export const apiSenderV2 = async (
  url: string,
  method: ApiMethods,
  data: any,
  formatResponse = true,
  token = null,
  options?: { signal?: AbortSignal; timeout?: number }
) => {
  const axiosInstance = axios.create({
    baseURL: useSystemStore.getState().baseUrl,
    timeout: options?.timeout,
  });
  const { getToken } = useAuthStore.getState();
  const authToken = token ?? (await getToken());
  if (!authToken) {
    throw new Error('authToken is not found');
  }
  const metaHeaders = getMetaHeaders();
  console.log('AZED metaHeaders', metaHeaders);
  try {
    // Update this to all methods
    let res;
    if (method === ApiMethods.GET) {
      res = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${authToken}`, ...metaHeaders },
        signal: options?.signal,
      });
    } else if (method === ApiMethods.POST) {
      res = await axiosInstance.post(url, data, {
        headers: { Authorization: `Bearer ${authToken}`, ...metaHeaders },
        signal: options?.signal,
      });
    } else if (method === ApiMethods.PUT) {
      res = await axiosInstance.put(url, data, {
        headers: { Authorization: `Bearer ${authToken}`, ...metaHeaders },
        signal: options?.signal,
      });
    } else if (method === ApiMethods.DELETE) {
      res = await axiosInstance.delete(url, {
        headers: { Authorization: `Bearer ${authToken}`, ...metaHeaders },
        data, // Include request body for DELETE requests
        signal: options?.signal,
      });
    } else if (method === ApiMethods.PATCH) {
      res = await axiosInstance.patch(url, data, {
        headers: { Authorization: `Bearer ${authToken}`, ...metaHeaders },
        signal: options?.signal,
      });
    } else {
      throw new Error('Method not supported');
    }
    // const res = await axiosInstance.(url, data, {
    //   headers: { Authorization: `Bearer ${authToken}` },
    // });
    if (res?.status === 400) {
      return res.data;
    }

    if (res?.status >= 300) {
      return null;
    }
    const returnValue = formatResponse
      ? (res.data.results ?? res.data.result ?? res.data)
      : res.data;
    console.log('AZED apiSenderV2 returning:', {
      formatResponse,
      returnValue,
      resData: res.data,
      url,
    });
    return returnValue;
  } catch (error) {
    console.log('AZED found error in apiSenderV2', error);
    const axiosError = error as AxiosError;
    if (axiosError.isAxiosError) {
      console.log(
        'AZED the axios error',
        axiosError.response?.data,
        axiosError.response?.status,
        axiosError.response
      );
      return axiosError.response?.data;
    }
    // console.log("AZED found error results", error)
    return null;
  }
};
export const apiSenderWithErrors = async (url: string, _method: ApiMethods, data: any) => {
  const axiosInstance = axios.create({ baseURL: useSystemStore.getState().baseUrl });
  const { authToken } = useAuthStore.getState();
  if (!authToken) {
    throw new Error('authToken is not found');
  }

  const res = await axiosInstance.post(url, data, {
    headers: { Authorization: `Bearer ${authToken}`, ...getMetaHeaders() },
  });
  // const res = await axiosInstance(url, {
  //   method,
  //   data,
  //   headers: { Authorization: `Bearer ${authToken}` },
  // });
  if (res?.status >= 300) {
    return null;
  }
  return res.data.results;
};
// ----------------------------------------------------------------------

export enum ApiMethods {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  PATCH = 'PATCH',
}

// ----------------------------------------------------------------------
export const apiEndpoints = {
  server: {
    preloadData: '/api/server/preload',
  },
  auth: {
    user: '/api/auth/user',
    login: '/api/auth/login',
    signup: '/api/auth/signup',
  },
  admin: {
    fetchSales: '/api/admin/sales',
    fetchSingleSale: (salesId: string, businessId: string) =>
      `/api/admin/sales/${salesId}/${businessId}`,
    updateSale: (salesId: string) => `/api/admin/business/v2/sales/${salesId}`,
    deleteSale: (salesId: string) => `/api/admin/business/v2/sales/${salesId}`,
    restoreSale: (salesId: string) => `/api/admin/business/v2/sales/${salesId}/restore`,
    fetchUsers: '/api/admin/users',
    fetchBusinesses: '/api/admin/business',
    fetchBusinessesV2: '/api/admin/business/v2',
    fetchBusinessesV2Paginated: '/api/admin/business/v2/paginated',
    fetchBusinessesV3: '/api/admin/business/v3',
    fetchBusinessesV3Paginated: '/api/admin/business/v3/paginated',
    fetchBusinessV3ById: (id: string) => `/api/admin/business/v3/${id}`,
    registerTerminal: '/api/admin/business/v2/devices/register-terminal',
    linkTerminalToLocation: '/api/admin/business/v2/devices/linking',
    updateTerminal: (terminalId: string) => `/api/admin/business/v2/devices/${terminalId}`,
    updateBusiness: (id: string) => `/api/admin/business/v2/${id}`,
    fetchBusinessTransactions: (id: string) => `/api/admin/business/v2/${id}/transactions`,
    fetchSecureCode: (id: string) => `/api/admin/business/v2/${id}/secure-code`,
    fetchBusinessBalance: (id: string) => `/api/admin/business/v2/payouts/balance?businessId=${id}`,
    fetchBusinessPayouts: (id: string) => `/api/admin/business/v2/payouts?businessId=${id}`,
    createInstantPayout: '/api/admin/business/v2/payouts',
    togglePaymentAccountStatus: (id: string) =>
      `/api/admin/business/v2/${id}/payment-accounts/toggle`,
    importPaymentAccount: (id: string) => `/api/admin/business/v2/${id}/payment-accounts/import`,
    importStripePaymentAccount: (businessId: string) =>
      `/api/admin/business/v2/onboarding/stripe/${businessId}/import`,
    updatePaymentAccount: (businessId: string, paymentAccountId: string) =>
      `/api/admin/business/v2/${businessId}/payment-accounts/${paymentAccountId}`,
    fetchBlockChypCredentials: (businessId: string) =>
      `/api/admin/business/v2/${businessId}/payment-accounts/blockchyp/credentials`,
    generateBlockChypCredentials: (businessId: string) =>
      `/api/admin/business/v2/${businessId}/payment-accounts/blockchyp/credentials/generate`,
    // BlockChyp terminal endpoints
    fetchBlockChypTerminals: (businessId: string) =>
      `/api/admin/business/v2/${businessId}/blockchyp/terminals`,
    addBlockChypTerminal: (businessId: string) =>
      `/api/admin/business/v2/${businessId}/blockchyp/terminals`,
    activateBlockChypTerminal: (businessId: string) =>
      `/api/admin/business/v2/${businessId}/blockchyp/terminals/activate`,
    createPaxProviderAccount: (id: string) => `/api/admin/business/v2/${id}/pax-provider-accounts`,
    fetchSingleBusiness: (id: string) => `/api/admin/business/${id}`,
    linkUserToBusiness: (userId: string, businessId: string) =>
      `/api/admin/business/${businessId}/link-user/${userId}`,
    reportsByRange: '/api/admin/reports/daily/range',
    calculateDailySummary: '/api/admin/business/v2/daily-summary/calculate',
    createUser: '/api/admin/users/new-user',
    createBusiness: '/api/admin/business/new-business',
    linkUserAndCreateStp: '/api/admin/business/link-create-stp',
    createAccountV3: '/api/admin/business/v2/create-account',
    importAccount: '/api/admin/business/v2/import-account',
    createMinimalBusiness: '/api/admin/business/v2/onboarding/platform',
    createStripeAccount: '/api/admin/business/v2/onboarding/stripe',
    paymentsInternal: '/api/admin/payments/internal',
    paymentsExternal: '/api/admin/payments/external',
    accountPayouts: '/api/admin/business/v2/account-payouts',
    accountPayoutById: (id: string) => `/api/admin/business/v2/account-payouts/${id}`,
    allSales: '/api/admin/business/v2/sales',
    recalculateSales: '/api/admin/business/v2/sales/recalculate',
    processorEvents: '/api/admin/diagnostics/processor-events',
    rerunProcessorEvent: '/api/admin/diagnostics/processor-events/rerun',
    dailySummaries: '/api/admin/business/v2/daily-summary',
    updateDailySummary: '/api/admin/business/v2/daily-summary/update',
    manualOverrideDailySummary: '/api/admin/business/v2/daily-summary/manual-override',
    bulkRecalculateDailySummary: '/api/admin/business/v2/daily-summary/bulk-recalculate',
    monthlyDailySummary: '/api/admin/business/v2/daily-summary/month',
    // TOTP endpoints
    totpGenerate: '/api/admin/totp/generate',
    totpVerify: '/api/admin/totp/verify',
    totpDisable: '/api/admin/totp',
    totpSecrets: '/api/admin/totp',
    // Notification endpoints
    notifications: '/api/admin/notifications',
    resendNotification: (id: string) => `/api/admin/notifications/${id}/resend`,
    batchResendNotifications: '/api/admin/notifications/resend-batch',
    // Registration endpoints
    submitRegistration: '/api/admin/business/v2/registration/form',
    registrationSecureUploadLink: (registrationId: string) =>
      `/api/admin/business/v2/registration/form/secure-upload-link?registrationId=${encodeURIComponent(registrationId)}`,
    // Donate page setup
    setupDonatePage: '/api/admin/donate/setup',
    // Orphan payments endpoints
    orphanPayments: '/api/admin/orphan-payments',
    resolveOrphanPayment: (id: string) => `/api/admin/orphan-payments/${id}/resolve`,
    // Payment transactions endpoints
    paymentTransactions: '/api/admin/business/v2/payment-transactions',
    paymentTransactionById: (id: string) => `/api/admin/business/v2/payment-transactions/${id}`,
    // Processor transactions endpoint (for reconciliation)
    processorTransactions: '/api/admin/business/v2/payment-transactions/processor',
    // Sync processor transactions endpoint (manual sync from processor to database)
    syncProcessorTransactions: '/api/admin/business/v2/payment-transactions/sync',
    // Sync processor transactions by ID endpoint (sync specific transactions by their IDs)
    syncProcessorTransactionsById: '/api/admin/business/v2/payment-transactions/sync-by-id',
    // QuickBooks sales backfill endpoint (backfill historical daily sales into QuickBooks)
    quickbooksBackfill: '/api/admin/reports/quickbooks/backfill',
    // Reconciliation endpoint
    reconciliation: '/api/admin/reconciliation',
    reconciliationRepairPreview: '/api/admin/reconciliation/repair/preview',
    reconciliationRepairApply: '/api/admin/reconciliation/repair/apply',
    // Public business profile endpoints
    publicBusinessProfile: (businessId: string) => `/api/admin/business/v2/public/${businessId}`,
    // Marketplace admin endpoints
    marketplace: {
      distributors: '/api/admin/marketplace/distributors',
      distributorById: (id: string) => `/api/admin/marketplace/distributors/${id}`,
      products: '/api/admin/marketplace/products',
      productById: (id: string) => `/api/admin/marketplace/products/${id}`,
      productsImport: '/api/admin/marketplace/products/import',
      orders: '/api/admin/marketplace/orders',
      orderById: (id: string) => `/api/admin/marketplace/orders/${id}`,
      orderStatus: (id: string) => `/api/admin/marketplace/orders/${id}/status`,
    },
    // Auth V3 Device Management — read-only admin endpoints that power the Device
    // Management dashboard (summary counts + four paginated list tables). The claim
    // (approve) action lives in `apiEndpoints.businessDevicesV3.claim`.
    // See docs/authentication/device-management-admin-client.md.
    deviceManagement: {
      summary: '/api/admin/auth/v3/device-management/summary',
      pairingSessions: '/api/admin/auth/v3/device-management/pairing-sessions',
      businessDevices: '/api/admin/auth/v3/device-management/business-devices',
      paymentTerminals: '/api/admin/auth/v3/device-management/payment-terminals',
      businessLocations: '/api/admin/auth/v3/device-management/business-locations',
    },
  },
  checkout: {
    sale: '/api/sales',
    sale_v2: '/api/sales/v2',
    cancelSale: '/api/sales/v2/cancel-sale',
    manual_check: '/api/sales/manual-check',
    pay: '/api/sales/payment',
    fetch: '/api/sales',
    terminalCancel: '/api/sales/terminal/clear',
    terminalStatus: '/api/sales/terminal/status',
  },
  transactions: {
    fetch: '/api/sales',
    fetchV2: '/api/sales/v2',
    fetchV2Records: '/api/sales/v2/records',
    refundV2: '/api/sales/v2/refund',
    addCustomerV2: '/api/sales/v2/add-customer',
    fetchOne: '/api/sales/single',
    support: '/api/sales/support',
    paymentRefund: '/api/sales/payment/refund',
    ebt: '/api/transactions/ebt',
    saleStatusLightweight: '/api/sales/v2/status',
  },
  business: {
    fetchBusinessBalance: '/api/business/balance',
    support: '/api/business/support',
    fees: '/api/business/fees',
    configurations: '/api/business/configurations',
    itemCategories: '/api/business/item-categories',
    devices: (businessId: string) => `/api/business/devices/${businessId}`,
    companionDevice: '/api/business/devices/companion',
    blockchypCredentials: '/api/business/payment-accounts/blockchyp/credentials',
    paxCredentials: '/api/business/payment-accounts/pax/credentials',
    paymentRecheck: '/api/business/payment-recheck/by-sale',
    integrationsStatus: '/api/business/integrations/status',
  },
  devices: {
    pairClaim: (pairingCode: string) => `/api/auth/devices/pair/claim/${pairingCode}`,
  },
  // Auth V3 business-device pairing (see docs/authentication/business-device-pairing-v3-client.md).
  // `start` and `complete` are PUBLIC device-side endpoints; `poll` is the PUBLIC status endpoint the
  // device (and the admin tab) reads to watch a session progress; `claim` is the AUTHENTICATED
  // platform-admin endpoint that approves a pending pairing and binds it to a business.
  businessDevicesV3: {
    start: '/api/auth/v3/devices/pair',
    claim: '/api/admin/auth/v3/devices/claim',
    complete: '/api/auth/v3/devices/pair/complete',
    poll: (pollingId: string) => `/api/auth/v3/devices/pair/${pollingId}`,
  },
  terminal: {
    ctn_tkn: 'api/services/stp/ctn',
    linked_readers: 'api/services/terminal',
    wake: '/api/services/terminal/wake',
  },
  payouts: {
    fetch: '/api/payouts',
    fetchOne: '/api/payouts/single',
  },
  reports: {
    fetch: '/api/reports/daily-summary/v2',
    fetchMobileTopup: '/api/reports/mobile-topup',
    syncTransactions: '/api/reports/sync-transactions',
  },
  products: {
    searchIdentifier: '/api/inventory/search',
    create: '/api/inventory',
    base: '/api/inventory',
    deleteAll: '/api/inventory/all',
    sold: '/api/inventory/sold',
    massUpdatePrices: '/api/inventory/mass-update-prices',
    massUpdateCategoryAttributes: '/api/inventory/mass-update-category-attributes',
    changes: '/api/inventory/changes',
  },
  payment: {
    attachTPFAAS: '/api/sales/payment/tpfaas/attach',
  },
  customer: {
    base: '/api/customer',
  },
  mobileTopup: {
    base: '/api/mbs',
    fetchOne: '/api/mbs/single',
  },
  notify: {
    sendMessage: '/api/notify',
  },
  triggerPayment: '/api/services/stp/present-payment-method',
  quickbooks: {
    getConnectUrl: '/api/services/quickbooks/connect-url',
    getReauthUrl: '/api/services/quickbooks/reauth-url',
    disconnect: '/api/services/quickbooks/disconnect',
    status: '/api/services/quickbooks/status',
    callback: '/api/services/quickbooks/callback',
  },
  paymentLinks: {
    fetch: '/api/sales/payment/link',
    update: (id: string) => `/api/sales/payment/link/${id}`,
    cancel: (id: string) => `/api/sales/payment/link/${id}`,
  },
  // Business Marketplace endpoints (for Sultani Marketplace app)
  marketplace: {
    // Distributors
    distributors: '/api/marketplace/distributors',
    distributorById: (id: string) => `/api/marketplace/distributors/${id}`,
    distributorProducts: (id: string) => `/api/marketplace/distributors/${id}/products`,

    // Products
    products: '/api/marketplace/products',
    productById: (id: string) => `/api/marketplace/products/${id}`,
    productSearch: '/api/marketplace/products/search',

    // Orders
    orders: '/api/marketplace/orders',
    orderById: (id: string) => `/api/marketplace/orders/${id}`,
    orderCancel: (id: string) => `/api/marketplace/orders/${id}/cancel`,

    // Recurring Orders
    recurringOrders: '/api/marketplace/recurring-orders',
    recurringOrderById: (id: string) => `/api/marketplace/recurring-orders/${id}`,
    recurringOrderRun: (id: string) => `/api/marketplace/recurring-orders/${id}/run`,
    recurringOrderPause: (id: string) => `/api/marketplace/recurring-orders/${id}/pause`,
    recurringOrderResume: (id: string) => `/api/marketplace/recurring-orders/${id}/resume`,

    // Notifications
    notifications: '/api/notifications',
    notificationsUnreadCount: '/api/notifications/unread-count',
    notificationMarkRead: (id: string) => `/api/notifications/${id}/read`,
    notificationsMarkAllRead: '/api/notifications/mark-all-read',
    notificationPreferences: '/api/notifications/preferences',
  },
};

// ----------------------------------------------------------------------
